// 3h + B-Final-Fix (P3) · Public-safe intake with discrete method chips.
// Owner addendum §2 — shows Upload / Multi-Upload / Photo / Scan chips.
// No admin methods (Attach Existing, Request Info). Public-safe MIME allowlist.
// 25 MB per file. ERF brand palette. Accessible.
import React, { useRef, useState } from "react";
import axios from "axios";
import { Upload, Files, Camera, ScanLine } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_MB = 25;
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.docx,.xlsx,.txt";
const ACCEPT_IMAGES = ".jpg,.jpeg,.png,.webp";

const NAVY = "#12233F";
const IVORY = "#F5F1E8";
const BRASS = "#C6A15B";

const CHIPS = [
  { key: "upload",       label: "Upload",       Icon: Upload,   multi: false, capture: null,          accept: ACCEPT,        title: "Attach one file" },
  { key: "multi_upload", label: "Multi-Upload", Icon: Files,    multi: true,  capture: null,          accept: ACCEPT,        title: "Attach several files at once" },
  { key: "photo",        label: "Photo",        Icon: Camera,   multi: false, capture: "environment", accept: ACCEPT_IMAGES, title: "Take a single photo (mobile camera)" },
  { key: "scan",         label: "Scan",         Icon: ScanLine, multi: true,  capture: "environment", accept: ACCEPT_IMAGES, title: "Scan a multi-page document (mobile camera)" },
];

export default function PublicIntake({ formSlug, onIdsChange }) {
  const inputRef = useRef(null);
  const [chip, setChip] = useState(null);
  const [items, setItems] = useState([]); // {id, file_name, size, method}
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const trigger = (c) => {
    setChip(c);
    setError(null);
    // Configure the hidden input for this chip then click it.
    const input = inputRef.current;
    if (!input) return;
    input.multiple = c.multi;
    input.accept = c.accept;
    if (c.capture) input.setAttribute("capture", c.capture);
    else input.removeAttribute("capture");
    input.value = "";
    input.click();
  };

  const upload = async (files) => {
    if (!files || !files.length) return;
    setBusy(true); setError(null);
    const next = [...items];
    // For Scan method, tag the batch with a shared scan-group-<id> so pages
    // are grouped in the Central Document Center for admin review.
    const scanGroupId = chip?.key === "scan"
      ? `scan-group-${(crypto?.randomUUID?.() || Date.now().toString(36))}`
      : null;
    for (const file of Array.from(files)) {
      if (file.size > MAX_MB * 1024 * 1024) {
        setError(`${file.name}: file exceeds ${MAX_MB} MB limit`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", file);
      if (formSlug) fd.append("form_slug", formSlug);
      if (chip?.key) fd.append("intake_method", chip.key);
      if (scanGroupId) fd.append("scan_group_id", scanGroupId);
      try {
        const r = await axios.post(`${API}/public/attachments/upload`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        next.push({
          id: r.data.attachment_id,
          file_name: r.data.file_name,
          size: r.data.size,
          method: chip?.key || "upload",
          scan_group_id: scanGroupId,
        });
      } catch (e) {
        const d = e?.response?.data?.detail;
        setError(`${file.name}: ${d?.code || d?.message || "upload failed"}`);
      }
    }
    setItems(next);
    const nextIds = next.map((it) => it.id);
    onIdsChange && onIdsChange(nextIds);
    setBusy(false);
  };

  const remove = (id) => {
    const next = items.filter((it) => it.id !== id);
    setItems(next);
    const nextIds = next.map((it) => it.id);
    onIdsChange && onIdsChange(nextIds);
  };

  return (
    <div
      data-testid="public-intake"
      style={{
        padding: 16, border: `1px dashed ${BRASS}`,
        borderRadius: 4, background: `var(--exec-bg-alt, ${IVORY})`,
      }}
    >
      <div
        style={{
          fontSize: 11, letterSpacing: "0.15em",
          textTransform: "uppercase",
          color: "var(--exec-body, #64748b)", marginBottom: 10,
        }}
      >Attach plans, photos, or documents (optional)</div>

      <div
        role="group"
        aria-label="Attachment method"
        data-testid="public-intake-chips"
        style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
      >
        {CHIPS.map(({ key, label, Icon, title }) => (
          <button
            key={key}
            type="button"
            data-testid={`public-intake-chip-${key}`}
            onClick={() => trigger(CHIPS.find((c) => c.key === key))}
            disabled={busy}
            title={title}
            aria-label={title}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "8px 14px", borderRadius: 9999, minHeight: 44,   // 44 px touch target (B5 part 2c, 390 px check)
              background: chip?.key === key ? NAVY : "#FFFFFF",
              color: chip?.key === key ? IVORY : NAVY,
              border: `1px solid ${BRASS}`,
              cursor: busy ? "wait" : "pointer",
              fontSize: 11, letterSpacing: 1,
              textTransform: "uppercase",
              fontFamily: "'Space Mono', monospace",
              opacity: busy ? 0.6 : 1,
            }}
          >
            <Icon size={14} aria-hidden="true" /> {label}
          </button>
        ))}
      </div>

      <input
        ref={inputRef}
        type="file"
        hidden
        data-testid="public-intake-file-input"
        onChange={(e) => upload(e.target.files)}
      />

      <div
        style={{
          marginTop: 10, fontSize: 11, color: "#64748b", lineHeight: 1.55,
        }}
      >
        Up to {MAX_MB} MB each. PDF, JPEG, PNG, WEBP, DOCX, XLSX, or TXT.
        Files are stored with your submission and reviewed by our team.
      </div>

      {error && (
        <div
          data-testid="public-intake-error"
          role="alert"
          style={{ marginTop: 8, color: "#B91C1C", fontSize: 12 }}
        >{error}</div>
      )}

      {items.length > 0 && (
        <div
          data-testid="public-intake-list"
          style={{ marginTop: 12, display: "grid", gap: 6 }}
        >
          {items.map((it) => (
            <div
              key={it.id}
              data-testid={`public-intake-chip-${it.id}`}
              style={{
                display: "flex", justifyContent: "space-between", gap: 8,
                background: "white", padding: "6px 10px", borderRadius: 3,
                fontSize: 12, border: "1px solid #cbd5e1",
              }}
            >
              <span>
                <span
                  style={{
                    display: "inline-block", padding: "1px 6px",
                    marginRight: 8, borderRadius: 9999,
                    background: `${BRASS}22`, color: `${NAVY}CC`,
                    fontSize: 10, fontFamily: "'Space Mono', monospace",
                    textTransform: "uppercase", letterSpacing: 0.6,
                  }}
                >{it.method || "upload"}</span>
                {it.file_name} · {(it.size / 1024).toFixed(1)} KB
              </span>
              <button
                type="button"
                onClick={() => remove(it.id)}
                data-testid={`public-intake-remove-${it.id}`}
                style={{
                  background: "none", border: "none", color: "#B91C1C",
                  cursor: "pointer", fontSize: 12,
                }}
              >Remove</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
